'use client'
import { ToggleSRPFloatingAIChatDesktop } from '@/src/components/common/srp/aiFilter/ToggleSRPFloatingAIChatDesktop'
import { SmartSortMessage } from '@/src/components/common/srp/smartMetrics/smartSortMessage'
import { SmartPreferenceBanner } from '@/src/components/common/srp/smartPreferenceBanner'
import TopSortCardsBadge from '@/src/components/common/srp/topSortCardsLabel/topSortCardsLabel'
import SignInPromoBannerWrapper from '@/src/components/common/signInPromoBanner'
import YellowBannerPromo from '@/src/components/common/srp/yellowBannerPromo'
import { FlightCard } from '@/src/components/desktop/srp/flightCard'
import { useAIFeatureEnableManager } from '@/src/hooks/AIFeatureEnableManager'
import { useGetSortedTopCards } from '@/src/hooks/srp/getGetSortedTopCards'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { FlightCard as FlightCardType } from '@/src/types/flight'
import { TranslationFunction } from '@/src/types/translations'
import { getSmartPreferenceSectionState } from '@/src/utils/srp/aiRecommendation/preferenceSectionState/smartPreferenceSections'
import { extractFlightCardProps, GetFlightDataReturn } from '@/src/utils/srp/flightResultsUtil'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Button, Card, Collapse, Typography } from '@mui/material'
import React, { useCallback, useDeferredValue, useMemo, useState } from 'react'
import ReactList from 'react-list'
import DateCarouselWrapper from '../dateCarousel/DateCarouselWrapper'

// Static style objects to avoid recreating them on every render
const containerBoxSx = { p: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff', borderRadius: 1.5 }
const headerBoxSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 1,
    borderColor: 'grey.200',
    pb: 1
}
const routeTypographySx = { fontWeight: 600, color: 'grey.700' }
const cardWrapperSx = { pb: 1 }
const cardSx = { borderRadius: 1, border: '1px solid #e0e0e0', overflow: 'hidden', boxShadow: 'none', p: 0, mx: 0 }
const mainFlightBoxSx = { px: 3, pt: 1.5, pb: 1.5 }
const expandedListContainerSx = { bgcolor: '#f5f8fa', pt: 1.5, pb: 1.5 }
const expandedItemBoxSx = { mx: 1.5, my: 1.5, p: 1.5, bgcolor: 'white', borderRadius: 1 }
const toggleMoreBoxBaseSx = {
    cursor: 'pointer',
    px: 3,
    background: '#f7f7f7',
    borderTop: '1px solid #eee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: 500,
    color: 'primary.main',
    fontSize: '0.90rem',
    height: 35,
    '&:hover': { background: '#f0f4ff' }
}
const innerListContainerSx = { px: 2 }

interface FlightResultsProps {
    groupedEntries: [string, FlightCardType[]][]
    smartPreferenceGroupedCards?: {
        preferred: [string, FlightCardType[]][]
        nonPreferred: [string, FlightCardType[]][]
    } | null
    getFlightData: (flight: FlightCardType) => GetFlightDataReturn
    route?: string
    onBookFlight?: (flight: FlightCardType) => void
    onViewDetails?: (flight: FlightCardType) => void
    onSortByPrice?: () => void
    // selectedDate: string
    // onDateChange: (date: string) => void
    sectors: any
    tCommon: TranslationFunction<'common'>
    tFlightDetails: TranslationFunction<'srpFlightDetails'>
    isBookingInProgress?: boolean
    loadingFlightKey?: string | null
    limitedView?: boolean
    limitedCount?: number
    showLoadMore?: boolean
    onLoadMore?: () => void
}

const FlightResults: React.FC<FlightResultsProps> = ({
    groupedEntries = [],
    smartPreferenceGroupedCards = null,
    getFlightData,
    route,
    onBookFlight,
    onViewDetails,
    onSortByPrice,
    // selectedDate,
    // onDateChange,
    sectors,
    tCommon,
    tFlightDetails,
    isBookingInProgress,
    loadingFlightKey,
    limitedView = false,
    limitedCount = 5,
    showLoadMore = false,
    onLoadMore
}) => {
    const { isPerPaxPricingEnabled, smartSortIdMetrics, selectedSortForSelectedJourneyLeg } = useSearchResults()
    const isSmartSort = selectedSortForSelectedJourneyLeg?.sortKey === 'smart'
    const { srpSmartFilterSort } = useAIFeatureEnableManager()

    // INP: defer the heavy results-list render off the interaction's critical path. A filter toggle /
    // sort change mutates the results cache; the filter/sort UI (a separate subtree) re-renders
    // urgently so its checkbox/tab flips instantly, while re-rendering this large virtualized list —
    // the expensive part — runs at a lower, interruptible priority (a fast follow-up tap isn't blocked).
    const deferredGroupedEntries = useDeferredValue(groupedEntries)
    const deferredSmartGroups = useDeferredValue(smartPreferenceGroupedCards)

    // Handle limited view for flight schedule page
    const displayGroupedEntries = useMemo(() => {
        if (limitedView) {
            return deferredGroupedEntries.slice(0, limitedCount)
        }
        return deferredGroupedEntries
    }, [deferredGroupedEntries, limitedView, limitedCount])
    const {
        primaryEntries,
        secondaryEntries,
        shouldShowBannerBeforePrimary,
        shouldShowBannerBeforeSecondary,
        bannerVariant
    } =
        getSmartPreferenceSectionState({
            preferredEntries: deferredSmartGroups?.preferred,
            nonPreferredEntries: deferredSmartGroups?.nonPreferred,
            fallbackEntries: displayGroupedEntries,
            isSmartSort
        })

    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

    const handleExpand = useCallback((groupKey: string) => {
        setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))
    }, [])

    const topSortedCards = useGetSortedTopCards()

    const renderExpandedSamePriceCards = useCallback(
        (groupKey: string, cards: FlightCardType[]) => (
            <>
                <Box sx={expandedListContainerSx}>
                    <ReactList
                        length={cards.length}
                        itemRenderer={(idx, key) => {
                            const flight = cards[idx]
                            const sortedTopCard = topSortedCards.find(
                                sortOption => sortOption?.card?.id === flight.id
                            )

                            const flightCard = (
                                <FlightCard
                                    {...extractFlightCardProps(
                                        flight,
                                        sectors,
                                        getFlightData,
                                        isPerPaxPricingEnabled
                                    )}
                                    onBook={() => onBookFlight?.(flight)}
                                    onViewDetails={() => onViewDetails?.(flight)}
                                    tCommon={tCommon}
                                    tFlightDetails={tFlightDetails}
                                    isBookingLoading={loadingFlightKey === flight.priceBreakup.fare.fk}
                                    isDisabled={
                                        isBookingInProgress &&
                                        loadingFlightKey !== flight.priceBreakup.fare.fk
                                    }
                                />
                            )

                            if (sortedTopCard) {
                                return (
                                    <TopSortCardsBadge
                                        key={key}
                                        label={sortedTopCard.label}
                                        isActive={!!sortedTopCard.isActive}
                                        sortOptionId={sortedTopCard.id}
                                        isMobile={false}
                                        isCombined={sortedTopCard.isCombined}
                                    >
                                        <Box sx={expandedItemBoxSx}>{flightCard}</Box>
                                    </TopSortCardsBadge>
                                )
                            }

                            return <Box key={key} sx={expandedItemBoxSx}>{flightCard}</Box>
                        }}
                    />
                </Box>
                <Box onClick={() => handleExpand(groupKey)} sx={toggleMoreBoxBaseSx}>
                    {tFlightDetails('hideMoreOption')}
                </Box>
            </>
        ),
        [
            getFlightData,
            handleExpand,
            isBookingInProgress,
            loadingFlightKey,
            onBookFlight,
            onViewDetails,
            sectors,
            tCommon,
            tFlightDetails,
            topSortedCards
        ]
    )




    // const renderTopSortedCard = useCallback(() => {
    //     return (
    //         <Box p={2} borderRadius={1} bgcolor={"#F3F3F3"} mb={1} display={"flex"} flexDirection={"column"} gap={1}>
    //             {quickestCheapestCard.map(sortOptions => (
    //                 <CheapestQuickestNewBadge
    //                     key={sortOptions.id}
    //                     label={sortOptions.label}
    //                     isActive={!!sortOptions.isActive}
    //                     sortOptionId={sortOptions.id}
    //                     isMobile={false}
    //                     isCombined={sortOptions.isCombined}
    //                 >
    //                     <Card sx={{ ...cardSx, position: 'relative' }}>
    //                         <Box height={"10px"}></Box>
    //                         <YellowBannerPromo promos={sortOptions?.card?.promos} />
    //                         <>
    //                             <Box
    //                                 sx={{
    //                                     ...mainFlightBoxSx,
    //                                     pt: Number(sortOptions?.card?.promos?.length) > 0 ? 2.5 : 1.5
    //                                 }}
    //                             >
    //                                 <FlightCard
    //                                     {...extractFlightCardProps(
    //                                         sortOptions?.card!,
    //                                         sectors,
    //                                         getFlightData,
    //                                         isPerPaxPricingEnabled
    //                                     )}
    //                                     smartMetrics={smartSortIdMetrics?.[sortOptions?.card?.id!]}
    //                                     onBook={() => onBookFlight?.(sortOptions?.card!)}
    //                                     onViewDetails={() => onViewDetails?.(sortOptions?.card!)}
    //                                     tCommon={tCommon}
    //                                     tFlightDetails={tFlightDetails}
    //                                     isBookingLoading={loadingFlightKey === sortOptions?.card?.priceBreakup.fare.fk}
    //                                     isDisabled={
    //                                         isBookingInProgress &&
    //                                         loadingFlightKey !== sortOptions?.card?.priceBreakup.fare.fk
    //                                     }
    //                                 />
    //                             </Box>
    //                         </>
    //                     </Card>
    //                 </CheapestQuickestNewBadge>
    //             ))}
    //         </Box>
    //     )
    // }, [
    //     getFlightData,
    //     onBookFlight,
    //     onViewDetails,
    //     sectors,
    //     tCommon,
    //     tFlightDetails,
    //     loadingFlightKey,
    //     isBookingInProgress
    // ])

    const renderMainCard = useCallback(
        (entries: [string, FlightCardType[]][], index: number, key: React.Key) => {
            const [groupKey, cards] = entries[index]
            const isExpanded = expandedGroups[groupKey]
            const mainCard = cards[0]
            const extraCards = cards.length > 1 ? cards.slice(1) : []


            const sortedTopCard = topSortedCards.find(sortOption => sortOption?.card?.id === mainCard.id)
            // 4th slot in primary SRP list
            const signInPromoSlot = index === 3 ? <SignInPromoBannerWrapper /> : null

            if (sortedTopCard) {
                return (
                    <React.Fragment key={key}>
                        {signInPromoSlot}
                        <Box mb={isSmartSort ? 2 : 1}>
                    <TopSortCardsBadge
                        label={sortedTopCard.label}
                        isActive={!!sortedTopCard.isActive}
                        sortOptionId={sortedTopCard.id}
                        isMobile={false}
                        isCombined={sortedTopCard.isCombined}
                    >
                        <Card sx={{ ...cardSx, position: 'relative' }}>
                            <Box height={"10px"}></Box>
                            <YellowBannerPromo promos={sortedTopCard?.card?.promos} />
                            <>
                                <Box
                                    sx={{
                                        ...mainFlightBoxSx,
                                        pt: Number(sortedTopCard?.card?.promos?.length) > 0 ? 2.5 : 1.5
                                    }}
                                >
                                    <FlightCard
                                        {...extractFlightCardProps(
                                            sortedTopCard?.card!,
                                            sectors,
                                            getFlightData,
                                            isPerPaxPricingEnabled
                                        )}
                                        smartMetrics={smartSortIdMetrics?.[sortedTopCard?.card?.id!]}
                                        onBook={() => onBookFlight?.(sortedTopCard?.card!)}
                                        onViewDetails={() => onViewDetails?.(sortedTopCard?.card!)}
                                        tCommon={tCommon}
                                        tFlightDetails={tFlightDetails}
                                        isBookingLoading={loadingFlightKey === sortedTopCard?.card?.priceBreakup.fare.fk}
                                        isDisabled={
                                            isBookingInProgress &&
                                            loadingFlightKey !== sortedTopCard?.card?.priceBreakup.fare.fk
                                        }
                                    />
                                </Box>
                            </>
                        </Card>
                    </TopSortCardsBadge>
                        </Box>
                    </React.Fragment>
                )
            }

            return (
                <React.Fragment key={key}>
                    {signInPromoSlot}
                    <Box sx={{ pb: isSmartSort ? 2 : 1 }}>
                    <Card sx={{ ...cardSx, position: 'relative' }}>
                        <YellowBannerPromo promos={mainCard.promos} />
                        <>
                            <Box sx={{ ...mainFlightBoxSx, pt: mainCard.promos?.length > 0 ? 2.5 : 1.5 }}>
                                <FlightCard
                                    smartMetrics={smartSortIdMetrics?.[mainCard.id]}
                                    {...extractFlightCardProps(
                                        mainCard,
                                        sectors,
                                        getFlightData,
                                        isPerPaxPricingEnabled
                                    )}
                                    onBook={() => onBookFlight?.(mainCard)}
                                    onViewDetails={() => onViewDetails?.(mainCard)}
                                    tCommon={tCommon}
                                    tFlightDetails={tFlightDetails}
                                    isBookingLoading={loadingFlightKey === mainCard.priceBreakup.fare.fk}
                                    isDisabled={
                                        isBookingInProgress && loadingFlightKey !== mainCard.priceBreakup.fare.fk
                                    }
                                />
                            </Box>
                            {extraCards.length > 0 && !isExpanded && (
                                <Box onClick={() => handleExpand(groupKey)} sx={toggleMoreBoxBaseSx}>
                                    +{extraCards.length} {tFlightDetails('moreOptionAvailiable')}
                                    <KeyboardArrowDownIcon fontSize="small" />
                                </Box>
                            )}
                        </>

                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderExpandedSamePriceCards(groupKey, extraCards)}
                        </Collapse>
                    </Card>
                    </Box>
                </React.Fragment>
            )
        },
        [
            expandedGroups,
            getFlightData,
            handleExpand,
            isBookingInProgress,
            isSmartSort,
            loadingFlightKey,
            onBookFlight,
            onViewDetails,
            sectors,
            tCommon,
            tFlightDetails,
            renderExpandedSamePriceCards,
            topSortedCards
        ]
    )



    return (
        <>
            {srpSmartFilterSort && <SmartSortMessage />}
            <Box sx={containerBoxSx}>
                <Box sx={headerBoxSx}>
                    <Typography variant="subtitle2" sx={routeTypographySx}>
                        {route}
                    </Typography>


                    <ToggleSRPFloatingAIChatDesktop />
                </Box>
                <DateCarouselWrapper />
                {/* <Box>
                {renderTopSortedCard()}
            </Box> */}
                <Box sx={innerListContainerSx}>
                    {shouldShowBannerBeforePrimary && (
                        <SmartPreferenceBanner tCommon={tCommon} variant={bannerVariant} />
                    )}
                    <ReactList
                        length={primaryEntries.length}
                        itemRenderer={(index, key) => renderMainCard(primaryEntries, index, key)}
                        threshold={300}
                    />
                    {shouldShowBannerBeforeSecondary && (
                        <SmartPreferenceBanner tCommon={tCommon} variant={bannerVariant} />
                    )}
                    <ReactList
                        length={secondaryEntries.length}
                        itemRenderer={(index, key) => renderMainCard(secondaryEntries, index, key)}
                        threshold={300}
                    />
                    {limitedView && showLoadMore && deferredGroupedEntries.length > limitedCount && (
                        <Box
                            p={2}
                            display={'flex'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            // borderRadius={1.5}
                            sx={{
                                backdropFilter: 'blur(8px)',
                                WebkitBackdropFilter: 'blur(8px)', // Safari support
                                backgroundColor: 'rgba(255, 255, 255, 0.635)',
                                // border: '1px solid #eee',
                                borderTop: '2px solid rgba(255, 255, 255, 0.2)'
                            }}
                        >
                            <Button variant="customVariantPrimary" onClick={onLoadMore}>
                                {tCommon('loadMore')}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    )
}

export { FlightResults }
export type { FlightResultsProps }
